import { useCallback, useEffect, useRef, useState } from "react";
import { LayoutGrid } from "lucide-react";
import { getAllFlags } from "../../../../api/controllerDashboardApis";
import type { FlagRecord } from "../../../../api/controllerDashboardApis";
import { FlagsPanel } from "./FlagsPane";
import { CourtCard } from "./CourtroomCard";
import { Timeline } from "./Timeline";
import { ROOMS } from "../helpers/constants";
import type { Flag, Toast } from "../helpers/types";
import type { RoomStatus } from "../helpers/types";
import { useCourtroomRooms } from "../../../../hooks/useCourtroomRooms";
import { recordingSocket } from "../../../../components/mediasoup/recordingSocket";
import logger from "../../../../utils/logger";
import { SummaryBar, Toasts } from "./ControlStrip";
import CourtroomPlayerModal from "../focused-dashbaord/CourtroomPlayerPage";

interface CourtroomGridProps {
  flags: Flag[];
  setFlags: React.Dispatch<React.SetStateAction<Flag[]>>;
}

export function getBlockedRoomMessage(room: (typeof ROOMS)[number]) {
  switch (room.status) {
    case "rec":
      return {
        msg: "Stream unavailable — recording only (restricted)",
        color: "#5B21B6",
      };

    case "paused":
      return {
        msg: "Room in recess — no live feed available",
        color: "#B45309",
      };

    case "offline":
      return {
        msg: `Room offline — ${room.offReason ?? "no feed"}`,
        color: "#6B7280",
      };

    default:
      return null;
  }
}

export function runRefreshCycle(
  setRefreshing: React.Dispatch<React.SetStateAction<boolean>>,
  setSnapTick: React.Dispatch<React.SetStateAction<number>>,
  refreshTimerRef: React.RefObject<ReturnType<typeof setTimeout> | null>,
) {
  setRefreshing(true);

  setSnapTick((prev) => prev + 1);

  refreshTimerRef.current = setTimeout(() => {
    setRefreshing(false);
  }, 500);
}

export function removeToast(
  id: number,
  setToasts: React.Dispatch<React.SetStateAction<Toast[]>>,
) {
  setToasts((prev) => prev.filter((toast) => toast.id !== id));
}

export function scheduleToastRemoval(
  id: number,
  setToasts: React.Dispatch<React.SetStateAction<Toast[]>>,
) {
  setTimeout(() => {
    removeToast(id, setToasts);
  }, 2_800);
}

function toFlag(r: FlagRecord): Flag {
  const rawDuration = r.segmentDuration;
  const isValidDuration = rawDuration && !rawDuration.includes("NaN");

  return {
    id: r._id,
    tp: r.flagType.toLowerCase().includes("pii") ? "pii" : "conduct",
    rv: r.flagStatus === "dismissed",
    flagStatus: r.flagStatus ?? "unresolved",
    tm: r.timeStamp
      ? new Date(r.timeStamp).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "—",
    rm: r.cnr ?? "—",
    tg: r.flagSubType,
    ds: r.description?.trim()
      ? r.description
      : `${r.flagType} · ${r.flagSubType}`,
    dr: isValidDuration ? rawDuration : "—",
    startTime: r.startTime,
    endTime: r.endTime,
  };
}

export default function CourtroomGrid({
  flags,
  setFlags,
}: Readonly<CourtroomGridProps>) {
  const [playing, setPlaying] = useState<number[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const { rooms, isLoading } = useCourtroomRooms();

  const [highlightFlagId, setHighlightFlagId] = useState<string | undefined>();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [roomSnapUrls, setRoomSnapUrls] = useState<Record<string, string>>({});
  const snapUrlsRef = useRef<Record<string, string>>({});
  const toastCounterRef = useRef(0);
  const [newFlagId, setNewFlagId] = useState<string | null>(null);
  const [liveSnapReady, setLiveSnapReady] = useState<Set<string>>(new Set());
  const [flagsPage, setFlagsPage] = useState(1);
  const [flagsHasMore, setFlagsHasMore] = useState(true);
  const [flagsLoadingMore, setFlagsLoadingMore] = useState(false);
  const [focusedRoom, setFocusedRoom] = useState<{
    id: string;
    hlsUrl?: string;
    status?: RoomStatus;
    cs?: string;
    jg?: string;
  } | null>(null);

  const STREAMING_BACKEND_URL = import.meta.env.VITE_APP_STREAM_SERVER;

  useEffect(() => {
    getAllFlags(1).then(({ data, meta }) => {
      setFlags(data.map(toFlag));
      setFlagsPage(1);
      setFlagsHasMore(1 * meta.limit < meta.total);
    });
  }, [setFlags]);

  const loadMoreFlags = useCallback(async () => {
    if (flagsLoadingMore || !flagsHasMore) return;

    setFlagsLoadingMore(true);
    try {
      const nextPage = flagsPage + 1;
      const { data, meta } = await getAllFlags(nextPage);

      setFlags((prev) => [...prev, ...data.map(toFlag)]);
      setFlagsPage(nextPage);
      setFlagsHasMore(nextPage * meta.limit < meta.total);
    } finally {
      setFlagsLoadingMore(false);
    }
  }, [flagsPage, flagsHasMore, flagsLoadingMore, setFlags]);

  useEffect(() => {
    const handleGetFlag = (response: any) => {
      const flagRecords: FlagRecord[] = Array.isArray(response)
        ? response
        : (response?.data ?? []);

      if (flagRecords.length === 0) return;

      const newest = flagRecords[0];
      const newFlag = toFlag(newest);

      setFlags((prev) => {
        const alreadyExists = prev.some((f) => f.id === newFlag.id);
        if (alreadyExists) return prev;

        return [newFlag, ...prev];
      });

      setNewFlagId(newFlag.id);
      setTimeout(() => setNewFlagId(null), 800);
    };

    recordingSocket.on("get-flag", handleGetFlag);
    return () => {
      recordingSocket.off("get-flag", handleGetFlag);
    };
  }, [setFlags]);

  useEffect(() => {
    const handleFlagUpdated = (payload: {
      id: string;
      resolved?: boolean;
      action?: "mask" | "mute" | "dismiss";
    }) => {
      setFlags((prev) =>
        prev.map((f) => {
          if (f.id !== payload.id) return f;

          let flagStatus = f.flagStatus;
          if (payload.action === "mask") flagStatus = "masked";
          else if (payload.action === "mute") flagStatus = "muted";
          else if (payload.action === "dismiss") flagStatus = "dismissed";

          return {
            ...f,
            flagStatus,
            rv: flagStatus === "dismissed" ? true : f.rv,
          };
        }),
      );
    };

    recordingSocket.on("flag_updated", handleFlagUpdated);
    return () => {
      recordingSocket.off("flag_updated", handleFlagUpdated);
    };
  }, [setFlags]);

  useEffect(() => {
    if (rooms.length === 0) return;
    const initialSnaps: Record<string, string> = {};
    const initialReady = new Set<string>();
    rooms.forEach((room) => {
      if (room.initialSnapUrl) {
        initialSnaps[room.id] = room.initialSnapUrl;
        if (room.status === "live" || room.status === "flagged") {
          initialReady.add(room.id);
        }
      }
    });

    setRoomSnapUrls((prev) => ({ ...initialSnaps, ...prev }));

    if (initialReady.size > 0) {
      setLiveSnapReady((prev) => new Set([...prev, ...initialReady]));
    }

    rooms.forEach((room) => {
      recordingSocket.emit("watch_courtroom", { courtRoomNo: room.id });
    });
  }, [rooms]);

  useEffect(() => {
    const handleSnapshot = (payload: {
      courtRoomNo: string;
      frameUrl: string;
    }) => {
      const url = `${STREAMING_BACKEND_URL}${payload.frameUrl}`;
      snapUrlsRef.current[payload.courtRoomNo] = url;
      setRoomSnapUrls((prev) => ({ ...prev, [payload.courtRoomNo]: url }));
      setLiveSnapReady((prev) => {
        if (prev.has(payload.courtRoomNo)) return prev;
        return new Set([...prev, payload.courtRoomNo]);
      });
    };
    setRoomSnapUrls({ ...snapUrlsRef.current });

    recordingSocket.on("snapshot_frame", handleSnapshot);
    return () => {
      recordingSocket.off("snapshot_frame", handleSnapshot);
    };
  }, []);

  const filteredRooms =
    activeFilter === "all"
      ? rooms
      : rooms.filter((room) => room.status === activeFilter);

  const addToast = useCallback((msg: string, col = "#1A56DB") => {
    toastCounterRef.current += 1;

    const id = toastCounterRef.current;

    setToasts((prev) => [
      ...prev,
      {
        id,
        msg,
        col,
      },
    ]);

    scheduleToastRemoval(id, setToasts);
  }, []);

  const handlePlay = useCallback((idx: number) => {
    setPlaying((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((i) => i !== idx);
      }

      return [...prev, idx];
    });
  }, []);

  const handleStop = useCallback((idx: number) => {
    setPlaying((prev) => prev.filter((i) => i !== idx));
  }, []);

  const handleCardClick = useCallback(
    (idx: number) => {
      const room = filteredRooms[idx];
      setFocusedRoom({
        id: room.id,
        hlsUrl: room.hlsUrl,
        status: room.status,
        cs: room.cs,
        jg: room.jg,
      });
    },
    [filteredRooms],
  );

  return (
    <>
      <div className="flex flex-col gap-3.5">
        {(() => {
          if (isLoading) {
            return (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="h-44 rounded-[10px] bg-gray-100 animate-pulse"
                  />
                ))}
              </div>
            );
          }
          if (filteredRooms.length === 0) {
            return (
              <div className="flex flex-col items-center py-10 text-gray-400 text-[13px] gap-2">
                <span style={{ fontSize: 28 }}>🏛️</span>
                <span>No courtrooms match this filter</span>
              </div>
            );
          }
          const visibleRooms = filteredRooms.filter((room) => {
            if (room.status === "live" || room.status === "flagged") {
              return liveSnapReady.has(room.id);
            }
            return true;
          });

          return (
            <>
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-800">
                  <LayoutGrid size={15} color="#1A56DB" aria-hidden="true" />
                  <span>Courtroom Overview</span>
                </div>
                <span className="cursor-pointer text-[12px] text-blue-600">
                  View all {rooms.length} rooms ↗
                </span>
              </div>

              <SummaryBar
                rooms={visibleRooms}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />

              {/* Cards */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {visibleRooms.map((room, idx) => (
                  <CourtCard
                    key={room.id}
                    room={room}
                    snapUrl={roomSnapUrls[room.id] ?? null}
                    idx={idx}
                    isPlaying={playing.includes(idx)}
                    onPlay={handlePlay}
                    onCardClick={handleCardClick}
                    onStop={() => handleStop(idx)}
                    roomFlags={flags.filter((f) => f.rm?.includes(room.id))}
                  />
                ))}
              </div>
            </>
          );
        })()}

        {/* Timeline + Flags — hamesha dikhenge */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div id="tlPan">
            <Timeline
              onFlagSegmentClick={(flagId) => {
                setHighlightFlagId(flagId);
                setTimeout(() => setHighlightFlagId(undefined), 2000);
              }}
              onEditArchive={() => {
                logger.log("Edit archive clicked");
              }}
            />
          </div>
          <div id="flagPan">
            <FlagsPanel
              flags={flags}
              setFlags={setFlags}
              highlightId={highlightFlagId}
              newFlagId={newFlagId}
              onToast={addToast}
              onLoadMore={loadMoreFlags}
              hasMore={flagsHasMore}
              loadingMore={flagsLoadingMore}
            />
          </div>
        </div>
      </div>

      <Toasts toasts={toasts} />
      {focusedRoom && (
        <CourtroomPlayerModal
          room={focusedRoom}
          onClose={() => setFocusedRoom(null)}
        />
      )}
    </>
  );
}
